//
// Copyright (c) 2011-2018 by Curalate, Inc.
//

import instagramAccountModule from '@instagram-account/instagram-account-module';
import {
  PROMOTED_POSTS_ELIGIBILITY_STATES,
  PROMOTED_POSTS_ITEM_TYPES,
  PROMOTED_POSTS_MEDIA_TYPES,
} from '@instagram-account/instagram-account-promoted-posts/promoted-posts-eligibility-constants';
import { InstagramMediaProductType } from '@models/IInstagramMediaItem';

export default class PromotedPostsEligibilityService {
  /**
   * Determines if a post is eligible to be promoted, and if so,
   * whether a promotion is already running.
   *
   * @returns A promotion eligibility status
   */
  public getPromotionEligibilityStatus(post, runningPromotions: Set<string>) {
    const itemType = post.itemType ? post.itemType.toUpperCase() : '';

    if (this.isPromotionRunning(post.cuid, runningPromotions)) {
      return PROMOTED_POSTS_ELIGIBILITY_STATES.PROMOTION_RUNNING;
    }

    if (post.isCopyrightedPost) {
      return PROMOTED_POSTS_ELIGIBILITY_STATES.NOT_ELIGIBLE;
    } else if (
      post.mediaProductType === InstagramMediaProductType.IGTV ||
      post.mediaProductType === InstagramMediaProductType.REELS
    ) {
      return PROMOTED_POSTS_ELIGIBILITY_STATES.NOT_ELIGIBLE;
    } else if (itemType === PROMOTED_POSTS_ITEM_TYPES.POST) {
      return this.postMediaIsEligibleForPromotion(post.media)
        ? PROMOTED_POSTS_ELIGIBILITY_STATES.ELIGIBLE
        : PROMOTED_POSTS_ELIGIBILITY_STATES.NOT_ELIGIBLE;
    } else if (itemType === PROMOTED_POSTS_ITEM_TYPES.GALLERY) {
      return this.galleryMediaIsEligibleForPromotion(post)
        ? PROMOTED_POSTS_ELIGIBILITY_STATES.GALLERY
        : PROMOTED_POSTS_ELIGIBILITY_STATES.NOT_ELIGIBLE;
    } else {
      return PROMOTED_POSTS_ELIGIBILITY_STATES.NOT_ELIGIBLE;
    }
  }

  private postMediaIsEligibleForPromotion(media): boolean {
    const mediaType = media.type ? media.type.toUpperCase() : '';
    // const imageWidth = media?.standardResolutionImage?.width || 0;
    // const imageHeight = media?.standardResolutionImage?.height || 0;

    if (mediaType === PROMOTED_POSTS_MEDIA_TYPES.IMAGE) {
      // DEV-52107 - Look into how we can get the needed image info to support this check
      // return imageWidth >= PROMOTED_POSTS_IMAGE_REQUIREMENTS.MIN_WIDTH &&
      //              imageHeight >= PROMOTED_POSTS_IMAGE_REQUIREMENTS.MIN_HEIGHT;
      return true;
    } else if (mediaType === PROMOTED_POSTS_MEDIA_TYPES.VIDEO) {
      // TODO: Add support for videos
      return false;
    } else {
      return false;
    }
  }

  private galleryMediaIsEligibleForPromotion(post): boolean {
    const firstMedia = post?.galleryMedia?.[0] || {};

    return this.postMediaIsEligibleForPromotion(firstMedia);
  }

  private isPromotionRunning(cuid, runningPromotions: Set<string>): boolean {
    return runningPromotions.has(cuid);
  }
}

instagramAccountModule.service('PromotedPostsEligibilityService', [
  PromotedPostsEligibilityService,
]);
